// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useState } from 'react';
import './AgenticSearch.css';

function AgenticSearch() {
  const [searchMode, setSearchMode] = useState<'agent' | 'text'>('agent');
  const [siteFilter, setSiteFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Hardcoded sites - will be from API later
  const sites = [
    'DC-East-01',
    'DC-West-02',
    'DC-Central-03',
    'DC-North-04',
    'DC-South-05',
    'DC-Asia-06',
    'DC-Europe-07',
    'DC-Canada-08',
  ];

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Search functionality will be implemented by another team
    console.log('Search submitted:', {
      mode: searchMode,
      site: siteFilter,
      query: searchQuery,
    });
  };

  return (
    <div className="search-page">
      <div className="search-container">
        <div className="page-header">
          <div>
            <h1 className="page-title">Agentic Search</h1>
            <p className="page-subtitle">
              {searchMode === 'agent'
                ? 'AI-powered intelligent search across all resources'
                : 'Text-based keyword search'}
            </p>
          </div>
        </div>

        {/* Filters Section */}
        <div className="search-filters">
          <div className="mode-toggle">
            <button
              className={`toggle-btn ${searchMode === 'agent' ? 'toggle-active' : ''}`}
              onClick={() => setSearchMode('agent')}
            >
              🤖 AI Agent Mode
            </button>
            <button
              className={`toggle-btn ${searchMode === 'text' ? 'toggle-active' : ''}`}
              onClick={() => setSearchMode('text')}
            >
              📝 Text Search
            </button>
          </div>

          <div className="filter-group">
            <label className="filter-label">Site:</label>
            <select
              value={siteFilter}
              onChange={e => setSiteFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Sites</option>
              {sites.map(site => (
                <option key={site} value={site}>
                  {site}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Search Form */}
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-input-container">
            <textarea
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={
                searchMode === 'agent'
                  ? 'Ask anything... e.g., "Show me all sites with efficiency below 90%" or "What are the top alerts this week?"'
                  : 'Enter keywords... e.g., "power consumption", "cooling system", "alerts"'
              }
              className="search-textarea"
              rows={4}
            />
          </div>

          <div className="search-actions">
            <button
              type="button"
              className="btn-clear"
              onClick={() => setSearchQuery('')}
              disabled={!searchQuery}
            >
              Clear
            </button>
            <button type="submit" className="btn-search" disabled={!searchQuery.trim()}>
              {searchMode === 'agent' ? '🔍 Ask AI' : '🔍 Search'}
            </button>
          </div>
        </form>

        {/* Results Placeholder */}
        <div className="search-results">
          {searchQuery ? (
            <div className="results-placeholder">
              <div className="placeholder-icon">{searchMode === 'agent' ? '🤖' : '📝'}</div>
              <p className="placeholder-title">Search Ready</p>
              <p className="placeholder-text">
                Click "{searchMode === 'agent' ? 'Ask AI' : 'Search'}" to execute your query.
              </p>
              <div className="placeholder-details">
                <div className="detail-item">
                  <span className="detail-label">Mode:</span>
                  <span className="detail-value">
                    {searchMode === 'agent' ? 'AI Agent' : 'Text Search'}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Site:</span>
                  <span className="detail-value">{siteFilter === 'all' ? 'All' : siteFilter}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Query Length:</span>
                  <span className="detail-value">{searchQuery.length} characters</span>
                </div>
              </div>
              <p className="placeholder-note">
                Search backend and result processing will be implemented by another team.
              </p>
            </div>
          ) : (
            <div className="empty-results">
              <div className="empty-icon">🔍</div>
              <p className="empty-title">Start Your Search</p>
              <p className="empty-text">
                {searchMode === 'agent'
                  ? 'Use natural language to ask questions about your sites, resources, and operations.'
                  : 'Enter keywords to search across sites, assets, users, and system logs.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AgenticSearch;
